import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, FileText, Package, Truck, MapPin, Camera } from 'lucide-react';
import { PhotoGalleryModal } from './PhotoGalleryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  received: { label: 'RECIBIDO', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  in_process: { label: 'EN PROCESO', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  invoiced: { label: 'FACTURADO', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  shipped: { label: 'ENVIADO', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  delivered: { label: 'ENTREGADO', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
};

const WarehouseDetail = ({ token, wrId, wrData, clientData, onBack, onNavigate }) => {
  const [wr, setWr] = useState(wrData || null);
  const [client, setClient] = useState(clientData || null);
  const [trackings, setTrackings] = useState([]);
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState([]);

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!wrData && wrId) {
      axios.get(`${API}/admin/warehouse-receipts`, axiosConfig).then(res => {
        const found = res.data.find(w => w.id === wrId);
        if (found) setWr(found);
      }).catch(() => {});
    }
  }, [wrId]);

  useEffect(() => {
    if (!wr) return;
    // Fetch trackings that belong to this WR
    axios.get(`${API}/admin/trackings`, axiosConfig).then(res => {
      const wrTrackings = res.data.filter(t =>
        wr.tracking_ids?.includes(t.id) ||
        wr.tracking_numbers?.includes(t.tracking_number) ||
        t.warehouse_id === wr.id ||
        t.warehouse_receipt_id === wr.id
      );
      setTrackings(wrTrackings);
    }).catch(() => {});
    // Fetch client
    if (!client && wr.client_id) {
      axios.get(`${API}/admin/clients`, axiosConfig).then(res => {
        setClient(res.data.find(c => c.id === wr.client_id));
      }).catch(() => {});
    }
    // Fetch linked invoice for this WR
    axios.get(`${API}/admin/invoices`, axiosConfig).then(res => {
      const inv = res.data.find(i =>
        i.warehouse_receipt_id === wr.id ||
        i.guias?.includes(wr.wr_number)
      );
      if (inv) setLinkedInvoice(inv);
    }).catch(() => {});
  }, [wr]);

  const openPhotoGallery = async (trackingId) => {
    try {
      const res = await axios.get(`${API}/packages/${trackingId}/images`, axiosConfig);
      if (res.data?.length > 0) {
        setGalleryFiles(res.data);
        setGalleryOpen(true);
      } else {
        alert('Este paquete no tiene fotos');
      }
    } catch {
      alert('Error al cargar fotos');
    }
  };

  if (!wr) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  const status = statusConfig[wr.status] || statusConfig.received;
  const wrDate = wr.created_at ? new Date(wr.created_at).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="wr-detail">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm mb-2">
        <ArrowLeft size={16} /> Volver a Warehouses
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{wr.wr_number || 'WR'}</h1>
          <p className="text-gray-500 mt-1">{wrDate}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`${status.bg} ${status.text} ${status.border} border rounded-full px-4 py-1.5 text-xs font-semibold`}>
            {status.label}
          </span>
          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/warehouse-receipts/${wr.id}/pdf`, '_blank')}>
            <FileText size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Meta Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Método de Envío', value: wr.shipping_method || 'Aéreo', icon: <Truck size={16} className="text-blue-500" /> },
          { label: 'Peso Total', value: `${wr.final_weight_lb || wr.weight_lb || 0} lb`, icon: <Package size={16} className="text-orange-500" /> },
          { label: 'Piezas', value: wr.pieces || trackings.length || '-', icon: <Package size={16} className="text-green-500" /> },
          { label: 'Factura', value: linkedInvoice ? linkedInvoice.invoice_number : 'Sin factura', icon: <FileText size={16} className="text-purple-500" />,
            onClick: linkedInvoice ? () => onNavigate('invoice-detail', { invoiceId: linkedInvoice.id, invoiceData: linkedInvoice }) : null },
        ].map((item, i) => (
          <Card key={i} className="border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">{item.icon} {item.label}</div>
              {item.onClick ? (
                <button onClick={item.onClick} className="text-blue-600 hover:underline font-semibold text-sm">{item.value}</button>
              ) : (
                <p className="font-semibold text-gray-900">{item.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Remitente / Destinatario */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} className="text-blue-500" />
              <h3 className="font-semibold text-gray-900">REMITENTE</h3>
            </div>
            <div className="space-y-2 text-sm">
              <p className="font-medium">TRAETELO D' AFUERA</p>
              <p className="text-gray-600">8438 NW 66TH STREET</p>
              <p className="text-gray-600">MIAMI, FLORIDA 33166</p>
              <p className="text-gray-600">USA</p>
              <p className="text-gray-600">+1-786-828-3898</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} className="text-orange-500" />
              <h3 className="font-semibold text-gray-900">DESTINATARIO</h3>
            </div>
            <div className="space-y-2 text-sm">
              <p className="font-medium">{client?.full_name || wr.client_name || '-'}</p>
              <p className="text-gray-600">{wr.destination_address || client?.delivery_address || client?.address || '-'}</p>
              <p className="text-gray-600">{wr.destination_city || client?.delivery_city || client?.city || ''} {wr.destination_state || client?.delivery_state || client?.state || ''}</p>
              <p className="text-gray-600">Venezuela</p>
              <p className="text-gray-600">{client?.phone ? `${client.phone_country_code || '+58'} ${client.phone}` : '-'}</p>
              {client?.id_document && <p className="text-gray-500 text-xs">Cédula: {client.id_document}</p>}
              <p className="text-gray-500 text-xs">{client?.email || '-'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trackings Table */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package size={18} className="text-blue-500" />
            Paquetes del WR ({trackings.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left p-3 font-medium text-gray-600">#</th>
                  <th className="text-left p-3 font-medium text-gray-600">Tracking</th>
                  <th className="text-left p-3 font-medium text-gray-600">Descripción</th>
                  <th className="text-left p-3 font-medium text-gray-600">Peso</th>
                  <th className="text-left p-3 font-medium text-gray-600">Estado</th>
                </tr>
              </thead>
              <tbody>
                {trackings.map((t, i) => (
                  <tr key={t.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3 text-gray-500">{i + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-medium">{t.tracking_number}</span>
                        <button onClick={() => openPhotoGallery(t.id)} className="text-orange-500 hover:text-orange-600" title="Ver fotos">
                          <Camera size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">{t.description || '-'}</td>
                    <td className="p-3">{t.weight ? `${t.weight} lb` : '-'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.consolidated ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {t.consolidated ? 'Procesado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
                {trackings.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">No hay trackings asociados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <PhotoGalleryModal isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} files={galleryFiles} token={token} />
    </div>
  );
};

export default WarehouseDetail;
